import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataService } from '../services/dataService';
import { District, DistrictStats } from '../types';
import CantonMap from '../components/CantonMap';
import iceCreamIcon from '../assets/ice-cream-icon.png';

const HomePage: React.FC = () => {
  const [, setDistricts] = useState<District[]>([]);
  const [districtStats, setDistrictStats] = useState<DistrictStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredDistrict, setHoveredDistrict] = useState<DistrictStats | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [allDistricts, allStats, allMeasurements] = await Promise.all([
          DataService.getDistricts(),
          DataService.getDistrictStats(),
          DataService.getMeasurements(),
        ]);
        // Add "Alle Gemeinden" stats with the correct total count
        const alleGemeindenStats = {
          districtId: 0,
          districtName: 'Alle Gemeinden',
          totalMeasurements: allMeasurements.length, // Use actual total count (2699)
          avgMeasuredTemp: allStats.length > 0 ? allStats.reduce((sum, d) => sum + d.avgMeasuredTemp, 0) / allStats.length : 0,
          avgPerceivedTemp: allStats.length > 0 ? allStats.reduce((sum, d) => sum + d.avgPerceivedTemp, 0) / allStats.length : 0,
          maxTemp: allStats.length > 0 ? Math.max(...allStats.map(d => d.maxTemp)) : 0,
          minTemp: allStats.length > 0 ? Math.min(...allStats.map(d => d.minTemp)) : 0,
          locations: allStats.flatMap(d => d.locations)
        };
        
        setDistricts(allDistricts);
        setDistrictStats([alleGemeindenStats, ...allStats]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const onDistrictClick = (districtId: number) => {
    navigate(`/dashboard/${districtId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="text-center mb-8">
          {/* Ice Cream Icon */}
          <div className="mb-6">
            <img 
              src={iceCreamIcon} 
              alt="Ice Cream Icon" 
              className="h-24 w-auto mx-auto"
            />
          </div>
          
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            3-2-1 heiß!
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-16">
            Im KANTON LUZERN der Hitze auf der Spur. Wähle eine Gemeinde aus.
          </p>
        </div>

        {/* Canton Map */}
        <div className="mb-8">
          <CantonMap
            districtStats={districtStats}
            onDistrictClick={onDistrictClick}
            onDistrictHover={setHoveredDistrict}
          />
        </div>

        {/* Hover Statistics - Fixed positioning */}
        {hoveredDistrict && (
          <div className="fixed bottom-6 left-6 bg-white rounded-lg shadow-xl p-4 border border-gray-200 z-50 max-w-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {hoveredDistrict.districtName}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Standorte:</span>
                <span className="font-medium">{hoveredDistrict.locations.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Messungen:</span>
                <span className="font-medium">{hoveredDistrict.totalMeasurements}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Ø Temperatur:</span>
                <span className="font-medium">{hoveredDistrict.avgMeasuredTemp.toFixed(1)}°C</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Max. Temperatur:</span>
                <span className="font-medium">{hoveredDistrict.maxTemp.toFixed(1)}°C</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HomePage;
